import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/utils/api-client';
import { fetchLocationSuggestions } from '@/utils/google-maps';
import { PublicListing, PropertySearchParams, PropertySearchResponse } from '@/contexts/SearchContext';

// Query keys for property search
export const propertySearchKeys = {
  all: ['propertySearch'] as const,
  searches: () => [...propertySearchKeys.all, 'search'] as const,
  search: (params: PropertySearchParams) => [...propertySearchKeys.searches(), params] as const,
  featured: () => [...propertySearchKeys.all, 'featured'] as const,
  details: () => [...propertySearchKeys.all, 'detail'] as const,
  detail: (id: string) => [...propertySearchKeys.details(), id] as const,
};

// Search properties with pagination
export const usePropertySearch = (params: PropertySearchParams, enabled: boolean = true) => {
  return useQuery({
    queryKey: propertySearchKeys.search(params),
    queryFn: async (): Promise<PropertySearchResponse> => {
      const searchParams = new URLSearchParams();
      
      // Add search parameters
      if (params.location) searchParams.append('location', params.location);
      if (params.checkIn) searchParams.append('checkIn', params.checkIn);
      if (params.checkOut) searchParams.append('checkOut', params.checkOut);
      if (params.guests) searchParams.append('guests', params.guests.toString());
      if (params.priceMin) searchParams.append('priceMin', params.priceMin.toString());
      if (params.priceMax) searchParams.append('priceMax', params.priceMax.toString());
      if (params.propertyTypes?.length) {
        params.propertyTypes.forEach(type => searchParams.append('propertyTypes', type));
      }
      if (params.amenities?.length) {
        params.amenities.forEach(amenity => searchParams.append('amenities', amenity));
      }
      if (params.instantBook !== undefined) {
        searchParams.append('instantBook', params.instantBook.toString());
      }
      if (params.sortBy) searchParams.append('sortBy', params.sortBy);
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.offset) searchParams.append('offset', params.offset.toString());

      const endpoint = `/public/listings/search?${searchParams.toString()}`;
      const response = await apiClient.get<PropertySearchResponse>(endpoint);
      return response.data;
    },
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    keepPreviousData: true, // Keep previous results while loading new ones
  });
};

// Infinite scroll search for listings page
export const useInfinitePropertySearch = (params: PropertySearchParams, enabled: boolean = true) => {
  return useInfiniteQuery({
    queryKey: [...propertySearchKeys.search(params), 'infinite'],
    queryFn: async ({ pageParam = 0 }): Promise<PropertySearchResponse> => {
      const searchParams = new URLSearchParams();
      
      // Add search parameters
      if (params.location) searchParams.append('location', params.location);
      if (params.checkIn) searchParams.append('checkIn', params.checkIn);
      if (params.checkOut) searchParams.append('checkOut', params.checkOut);
      if (params.guests) searchParams.append('guests', params.guests.toString());
      if (params.priceMin) searchParams.append('priceMin', params.priceMin.toString());
      if (params.priceMax) searchParams.append('priceMax', params.priceMax.toString());
      if (params.propertyTypes?.length) {
        params.propertyTypes.forEach(type => searchParams.append('propertyTypes', type));
      }
      if (params.amenities?.length) {
        params.amenities.forEach(amenity => searchParams.append('amenities', amenity));
      }
      if (params.instantBook !== undefined) {
        searchParams.append('instantBook', params.instantBook.toString());
      }
      if (params.sortBy) searchParams.append('sortBy', params.sortBy);
      
      // Pagination parameters
      const limit = params.limit || 20;
      searchParams.append('limit', limit.toString());
      searchParams.append('offset', (pageParam * limit).toString());

      const endpoint = `/public/listings/search?${searchParams.toString()}`;
      const response = await apiClient.get<PropertySearchResponse>(endpoint);
      return response.data;
    },
    enabled,
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.hasMore ? allPages.length : undefined;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    keepPreviousData: true,
  });
};

// Get featured listings for homepage
export const useFeaturedListings = (limit: number = 12) => {
  return useQuery({
    queryKey: [...propertySearchKeys.featured(), { limit }],
    queryFn: async (): Promise<PublicListing[]> => {
      const endpoint = `/public/listings/featured?limit=${limit}`;
      const response = await apiClient.get<PublicListing[]>(endpoint);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Get single public listing detail
export const usePublicListing = (id: string) => {
  const isValidId = Boolean(id && id.trim() !== '' && id !== 'undefined' && id !== 'null');
  
  return useQuery({
    queryKey: propertySearchKeys.detail(id),
    queryFn: async (): Promise<PublicListing> => {
      if (!isValidId) {
        throw new Error('Invalid listing ID provided');
      }
      const response = await apiClient.get<PublicListing>(`/public/listings/${id}`);
      return response.data;
    },
    enabled: isValidId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Get available search filters (amenities, property types, price range)
export const useSearchFilters = () => {
  return useQuery({
    queryKey: [...propertySearchKeys.all, 'filters'],
    queryFn: async () => {
      const response = await apiClient.get<{
        amenities: string[];
        propertyTypes: string[];
        priceRange: { min: number; max: number };
      }>('/public/listings/filters');
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - filters don't change often
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Prefetch listing detail for hover/preview
export const usePrefetchListing = () => {
  const queryClient = useQueryClient();

  return (id: string) => {
    queryClient.prefetchQuery({
      queryKey: propertySearchKeys.detail(id),
      queryFn: async (): Promise<PublicListing> => {
        const response = await apiClient.get<PublicListing>(`/public/listings/${id}`);
        return response.data;
      },
      staleTime: 5 * 60 * 1000,
    });
  };
};

// Search suggestions for location autocomplete using Google Maps Places API (frontend)
export const useLocationSuggestions = (query: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: [...propertySearchKeys.all, 'locationSuggestions', query],
    queryFn: async (): Promise<string[]> => {
      return await fetchLocationSuggestions(query);
    },
    enabled: enabled && query.length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Custom hook for search state management with React Query integration
export const useSearchWithQuery = () => {
  const queryClient = useQueryClient();

  const invalidateSearchResults = () => {
    queryClient.invalidateQueries({ queryKey: propertySearchKeys.searches() });
  };

  const prefetchSearchResults = (params: PropertySearchParams) => {
    queryClient.prefetchQuery({
      queryKey: propertySearchKeys.search(params),
      queryFn: async (): Promise<PropertySearchResponse> => {
        const searchParams = new URLSearchParams();
        
        if (params.location) searchParams.append('location', params.location);
        if (params.checkIn) searchParams.append('checkIn', params.checkIn);
        if (params.checkOut) searchParams.append('checkOut', params.checkOut);
        if (params.guests) searchParams.append('guests', params.guests.toString());
        if (params.priceMin) searchParams.append('priceMin', params.priceMin.toString());
        if (params.priceMax) searchParams.append('priceMax', params.priceMax.toString());
        if (params.propertyTypes?.length) {
          params.propertyTypes.forEach(type => searchParams.append('propertyTypes', type));
        }
        if (params.amenities?.length) {
          params.amenities.forEach(amenity => searchParams.append('amenities', amenity));
        }
        if (params.instantBook !== undefined) {
          searchParams.append('instantBook', params.instantBook.toString());
        }
        if (params.sortBy) searchParams.append('sortBy', params.sortBy);
        if (params.limit) searchParams.append('limit', params.limit.toString());
        if (params.offset) searchParams.append('offset', params.offset.toString());

        const endpoint = `/public/listings/search?${searchParams.toString()}`;
        const response = await apiClient.get<PropertySearchResponse>(endpoint);
        return response.data;
      },
      staleTime: 2 * 60 * 1000,
    });
  };

  const getSearchResultsFromCache = (params: PropertySearchParams): PropertySearchResponse | undefined => {
    return queryClient.getQueryData(propertySearchKeys.search(params));
  };

  return {
    invalidateSearchResults,
    prefetchSearchResults,
    getSearchResultsFromCache,
  };
};